import { useEffect } from "react";
import { useIntl } from "react-intl";

import { toast } from "@/utils/toast";
import { logger } from "@/common/logger";

import {
  PASTE_COMMAND,
  COMMAND_PRIORITY_LOW,
  $createRangeSelection,
  $getSelection,
  $isRangeSelection,
  $caretRangeFromSelection,
  $removeTextFromCaretRange,
  $setSelectionFromCaretRange,
  $getRoot,
  $createParagraphNode,
  ParagraphNode,
} from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { createHeadlessEditor } from "@lexical/headless";
import { $insertDataTransferForRichText } from "@lexical/clipboard";
import {
  $convertToMarkdownString,
  $convertFromMarkdownString,
} from "@lexical/markdown";

import { ListItemNode, ListNode } from "@lexical/list";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { LinkNode } from "@lexical/link";
import { CodeNode } from "@lexical/code";
import { ImageNode } from "../nodes/ImageNode";

import { INPUT_TRANSFORMERS, TRANSFORMERS } from "../transformer";

const HEADLESS_EDITOR_NODES = Object.freeze([
  ListNode,
  ListItemNode,
  HorizontalRuleNode,
  HeadingNode,
  QuoteNode,
  TableCellNode,
  TableNode,
  TableRowNode,
  ImageNode,
  LinkNode,
  CodeNode,
]);

const PasteMarkdownPlugin = () => {
  const intl = useIntl();
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand<ClipboardEvent>(
      PASTE_COMMAND,
      (event) => {
        event.preventDefault();

        const clipboardData = event.clipboardData;
        if (!clipboardData) {
          logger.warn("No clipboard data found on paste event.");
          return false;
        }

        if (clipboardData.types.includes("application/x-lexical-editor")) {
          return false;
        }

        const types = clipboardData.types;

        const selection = $getSelection();
        if (!selection || !$isRangeSelection(selection)) {
          logger.warn("Unsupported or missing selection on paste.");
          return true;
        }

        const pasteHtml = types.includes("text/html");
        const pasteText = types.includes("text/plain");

        const handleMarkdownPaste = (markdown: string) => {
          editor.update(() => {
            const caretRange = $caretRangeFromSelection(selection);
            const root = $getRoot();
            let lastChild = root.getLastChild();

            if (!lastChild) {
              const paragraph = $createParagraphNode();
              root.append(paragraph);
              lastChild = paragraph;
            }

            const containerNode = lastChild.insertAfter(
              $createParagraphNode(),
            ) as ParagraphNode;

            $convertFromMarkdownString(
              markdown,
              TRANSFORMERS,
              containerNode,
              true,
            );

            const nodes = containerNode.getChildren();
            containerNode.remove();

            $setSelectionFromCaretRange(
              $removeTextFromCaretRange(caretRange),
            ).insertNodes(nodes);
          });
        };

        if (pasteHtml) {
          const headlessEditor = createHeadlessEditor({
            nodes: HEADLESS_EDITOR_NODES,
            onError: (error) => {
              logger.error("Headless editor error during paste:", error);
              toast.error(
                intl.formatMessage({
                  defaultMessage: "An error occurred while pasting",
                }),
              );
            },
          });

          headlessEditor.update(() => {
            try {
              $insertDataTransferForRichText(
                clipboardData,
                $createRangeSelection(),
                headlessEditor,
              );

              const markdown = $convertToMarkdownString(INPUT_TRANSFORMERS);
              handleMarkdownPaste(markdown);
            } catch (err) {
              logger.error("Error converting HTML clipboard to markdown:", err);
              toast.error(
                intl.formatMessage({
                  defaultMessage: "Paste failed to process HTML clipboard data",
                }),
              );
            }
          });

          return true;
        }

        if (pasteText) {
          const plain = clipboardData.getData("text/plain");
          handleMarkdownPaste(plain);
          return true;
        }

        logger.warn("Unrecognized clipboard format. No supported types found.");
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, intl]);

  return null;
};

export { PasteMarkdownPlugin };
